import { joursDepuis, LIBELLES_CATEGORIES, type CategorieDossier } from '@repere/core';
import { maintenant } from '../horloge';
import { dateComplete, heure } from './dates';

export interface Nouveaute {
  categorie: CategorieDossier;
  kind: string;
  compte: number;
  libelle: string;
  dernierLe: string;
  /**
   * Présent seulement pour une décision qui reste en attente d'une réponse — une
   * demande d'accord, une dépense déclarée. La date de la plus ancienne, pas de la
   * plus récente : c'est elle qu'on risque d'oublier.
   */
  depuisLe?: string;
}

/** « Depuis aujourd'hui », « Depuis hier », « Depuis N jours » — jamais « en retard ». */
function libelleAnciennete(depuisLe: string): string {
  const jours = joursDepuis(new Date(depuisLe), maintenant());
  if (jours <= 0) return "Depuis aujourd'hui";
  if (jours === 1) return 'Depuis hier';
  return `Depuis ${jours} jours`;
}

export interface Changements {
  depuis: string | null;
  parCategorie: Record<string, number>;
  nouveautes: Nouveaute[];
}

/**
 * Ce qui a changé dans le dossier depuis la dernière visite du parent qui regarde.
 *
 * Trois choses que ce bandeau ne fait pas, et qui ont demandé plus d'attention que ce
 * qu'il fait :
 *
 * - **Il ne compte jamais une absence.** Ses lignes viennent des notifications, dont la
 *   liste des sortes est fermée et arbitrée : aucune n'annonce une inaction de l'autre
 *   parent (principe anti-conflit n°6). « Une demande sans réponse depuis 4 jours » n'a
 *   donc aucun moyen d'apparaître ici, et c'est voulu.
 * - **Il ne nomme personne.** « 2 nouveaux messages », jamais « 2 messages de Camille » :
 *   dans un relevé de ce qui s'est accumulé, nommer l'autre parent le désigne comme la
 *   cause d'une charge.
 * - **Il ne dit rien de l'autre parent.** « Depuis votre dernière visite » se calcule sur
 *   un horodatage propre au lecteur, que l'autre ne peut ni lire ni déduire.
 *
 * Quand il n'y a rien, il disparaît : une ligne discrète répond à la question posée, sans
 * carte vide ni « 0 nouveauté ».
 */
export function Nouveautes({
  changements,
  surChoix,
}: {
  changements: Changements;
  surChoix: (categorie: CategorieDossier) => void;
}) {
  const depuis = changements.depuis ? new Date(changements.depuis) : null;
  const quand = depuis ? `${dateComplete(depuis)} à ${heure(depuis)}` : null;

  if (changements.nouveautes.length === 0) {
    return (
      <p className="discret nouveautes-vide">
        {quand
          ? `Rien de nouveau depuis votre dernière visite, ${quand}.`
          : 'Rien de nouveau pour le moment.'}
      </p>
    );
  }

  return (
    <section className="nouveautes" aria-labelledby="nouveautes-titre">
      <div className="nouveautes-entete">
        <h2 id="nouveautes-titre">Depuis votre dernière visite</h2>
        {quand ? <p className="discret">{quand}</p> : null}
      </div>

      <ul>
        {changements.nouveautes.map((nouveaute) => (
          <li key={nouveaute.kind}>
            <button type="button" onClick={() => surChoix(nouveaute.categorie)}>
              <span className="nouveautes-compte" aria-hidden="true">
                {nouveaute.compte > 99 ? '99+' : nouveaute.compte}
              </span>
              <span className="nouveautes-libelle">
                {nouveaute.libelle}
                {nouveaute.depuisLe ? (
                  <span className="nouveautes-ancien">{libelleAnciennete(nouveaute.depuisLe)}</span>
                ) : null}
              </span>
              <span className="discret nouveautes-ou">
                {LIBELLES_CATEGORIES[nouveaute.categorie]}
              </span>
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="m9 6 6 6-6 6" />
              </svg>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
